import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {CircularProgress} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {confirmSignIn} from 'aws-amplify/auth';
import React, {SyntheticEvent, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {PasswordChecker} from '../passwordchecker/passwordchecker';

type Props = {
  // Identifiant du compte : rendu dans un champ caché pour que le gestionnaire de mots de passe
  // rattache le nouveau mot de passe à ce compte (sinon il garde l'entrée « email + provisoire »).
  email: string;
  onConfirm: () => void;
};

export function SignupChangePass(props: Props) {
  const {t} = useTranslation();
  const [signupChangePassState, setSignupChangePassState] = useState({
    pass1: '',
    pass2: '',
    errorMessageNew: undefined as string | undefined,
    showPassword: false,
    state: 'idle' as 'idle' | 'loading' | 'invalidPassword' | 'error',
  });

  const {pass1, pass2, errorMessageNew, showPassword, state} = signupChangePassState;

  const handleClickShowPassword = () => {
    setSignupChangePassState({...signupChangePassState, showPassword: !showPassword});
  };

  const onConfirm = async () => {
    setSignupChangePassState({...signupChangePassState, state: 'loading', errorMessageNew: undefined});
    await confirmSignIn({challengeResponse: pass1})
      .then(() => {
        props.onConfirm();
      })
      .catch((error) => {
        console.error(error);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error.code === 'InvalidPasswordException') {
          setSignupChangePassState({
            ...signupChangePassState,
            state: 'invalidPassword',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            errorMessageNew: error.message,
          });
        } else {
          setSignupChangePassState({
            ...signupChangePassState,
            state: 'error',
            errorMessageNew: `unkown error: ${error}`,
          });
          throw error;
        }
      });
  };

  return (
    <Box
      component="form"
      role="form"
      onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
        // Vraie soumission pour que le navigateur la détecte, sans rechargement de page.
        event.preventDefault();
        void onConfirm();
      }}
    >
      <input type="text" name="username" autoComplete="username" value={props.email} readOnly hidden />
      <Box mb={2}>
        <TextField
          required
          fullWidth
          name="password"
          placeholder={t('form.password.placeholder')}
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="new-password"
          value={pass1}
          onChange={(event) => {
            setSignupChangePassState({
              ...signupChangePassState,
              state: 'idle',
              errorMessageNew: undefined,
              pass1: event.target.value,
            });
          }}
          error={errorMessageNew !== undefined}
          helperText={t('form.password.helperText')}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton
                  aria-label={t('form.password.togglevisibility.arialabel')}
                  sx={{p: 0}}
                  onClick={handleClickShowPassword}
                  size="small"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              ),
            },
          }}
        />
        {errorMessageNew !== undefined && (
          <Typography variant="body2" fontWeight="bold" color="error">
            {errorMessageNew}
          </Typography>
        )}
      </Box>
      <Box mb={2}>
        <TextField
          required
          fullWidth
          name="repeat-password"
          placeholder={t('form.passwordrepeat.placeholder')}
          type={showPassword ? 'text' : 'password'}
          id="repeat-password"
          autoComplete="new-password"
          value={pass2}
          onChange={(event) => {
            setSignupChangePassState({
              ...signupChangePassState,
              state: 'idle',
              errorMessageNew: undefined,
              pass2: event.target.value,
            });
          }}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton
                  aria-label={t('form.password.togglevisibility.arialabel')}
                  sx={{p: 0}}
                  onClick={handleClickShowPassword}
                  size="small"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              ),
            },
          }}
        />
      </Box>
      <PasswordChecker
        password={pass1}
        repeatPassword={pass2}
        onPasswordInvalid={() => {
          setSignupChangePassState({...signupChangePassState, state: 'invalidPassword'});
        }}
        onPasswordValid={() => {
          setSignupChangePassState({...signupChangePassState, state: 'idle'});
        }}
      />
      <Box mt={4} mb={1}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          disabled={state === 'invalidPassword' || pass1 !== pass2 || pass1.trim().length === 0}
        >
          {state === 'loading' ? <CircularProgress color="info" /> : t('signin.form.changepassbtn')}
        </Button>
      </Box>
    </Box>
  );
}
